import React, { useEffect } from 'react';
import { ValidationDeviceStatus } from '@/store/InstallationValidation/constants';
import BaseMobileDrawer from '../BaseMobileDrawer/BaseMobileDrawer';
import { useTranslation } from 'react-i18next';
import { CommentsSection, CommonInput, Description } from './MobileCommentDrawer.styles';
import { MobileCommentDrawerProps } from './MobileCommentDrawer.utils';
import { drawerRegistry } from '@/hooks/useScrollMovement';

const MobileCommentDrawer: React.FC<MobileCommentDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  loadStatus,
  unloadStatus,
  flowStatus,
  initialLoadComment = '',
  initialUnloadComment = '',
  initialFlowComment = '',
  isSubmitted,
}) => {
  const { t } = useTranslation();
  const [loadComment, setLoadComment] = React.useState(initialLoadComment);
  const [unloadComment, setUnloadComment] = React.useState(initialUnloadComment);
  const [flowComment, setFlowComment] = React.useState(initialFlowComment);

  useEffect(() => {
    if (isOpen) {
      drawerRegistry.closeMainDrawer();
    }
  }, [isOpen]);

  useEffect(() => {
    setLoadComment(initialLoadComment);
    setUnloadComment(initialUnloadComment);
    setFlowComment(initialFlowComment || '');
  }, [initialLoadComment, initialUnloadComment, initialFlowComment]);

  const isLoadFailed = loadStatus === ValidationDeviceStatus.FAILED;
  const isFlowFailed = flowStatus === ValidationDeviceStatus.FAILED;
  const isUnloadFailed = unloadStatus === ValidationDeviceStatus.FAILED;

  const isLoadNotTested = loadStatus === ValidationDeviceStatus.NOT_TESTED;
  const isUnloadNotTested = unloadStatus === ValidationDeviceStatus.NOT_TESTED;
  const isFlowNotTested = flowStatus === ValidationDeviceStatus.NOT_TESTED;

  const shouldShowLoadComment = isLoadFailed || isLoadNotTested;
  const shouldShowUnloadComment = isUnloadFailed || isUnloadNotTested;
  const shouldShowFlowComment = isFlowFailed || isFlowNotTested;

  const handleSave = () => {
    onSave(loadComment, unloadComment, flowComment);
    onClose();
  };

  const renderContent = () => (
    <>
      <Description>
        {t('commentsInfo')}
        <br />
        <br />
        <div className="subtitle">{title}</div>
      </Description>
      <CommentsSection>
        {shouldShowLoadComment && (
          <div className="input-group">
            <div className="label">{isLoadFailed ? t('loadTestFailed') : t('loadTestNotTested')}</div>
            <CommonInput
              placeholder={t('ADD_COMMENT')}
              value={loadComment}
              onChange={e => setLoadComment(e.target.value)}
              disabled={isSubmitted}
            />
          </div>
        )}
        {shouldShowUnloadComment && (
          <div className="input-group">
            <div className="label">{isUnloadFailed ? t('unloadTestFailed') : t('unloadTestNotTested')}</div>
            <CommonInput
              placeholder={t('ADD_COMMENT')}
              value={unloadComment}
              onChange={e => setUnloadComment(e.target.value)}
              disabled={isSubmitted}
            />
          </div>
        )}
        {shouldShowFlowComment && (
          <div className="input-group">
            <div className="label">{isFlowFailed ? t('flowTestFailed') : t('flowTestNotTested')}</div>
            <CommonInput
              placeholder={t('ADD_COMMENT')}
              value={flowComment}
              onChange={e => setFlowComment(e.target.value)}
              disabled={isSubmitted}
            />
          </div>
        )}
      </CommentsSection>
    </>
  );

  return (
    <BaseMobileDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('comment')}
      isSubmitted={isSubmitted}
      primaryButton={{
        text: initialLoadComment || initialUnloadComment ? t('SAVE_CHANGES') : t('Save'),
        onClick: handleSave,
        disabled: isSubmitted,
      }}
    >
      {renderContent()}
    </BaseMobileDrawer>
  );
};

export default MobileCommentDrawer;
